import Seo from "@/components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageMasthead from "@/components/PageMasthead";
import ResponsiveAdDemo from "@/components/ResponsiveAdDemo";
import { AdGuidelines } from "@/components/AdGuidelines";
import GifHowTo from "@/components/GifHowTo";
import { FadeUp } from "@/components/motion";
import useApi from "@/hooks/useApi";
import { BRAND_DETAILS } from "@/content/brands";

const WSA_FALLBACK = BRAND_DETAILS.whichschooladvisor.ad_guidelines;
const SC_FALLBACK = BRAND_DETAILS.schoolscompared.ad_guidelines;

const jump = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const AdSizes = () => {
  const { data: brands } = useApi("/brands");
  const list = brands || [];
  const byName = (n) => list.find((b) => b.name === n);
  const WSA = byName("WhichSchoolAdvisor")?.ad_guidelines || WSA_FALLBACK;
  const SC = byName("SchoolsCompared")?.ad_guidelines || SC_FALLBACK;

  return (
  <div data-testid="ad-sizes-page">
    <Seo
      title="Ad Sizes & Specifications"
      path="/advertising"
      description="Media pack for WhichSchoolAdvisor.com and SchoolsCompared.com — technical specs, ad positions, Jumbo and Elegant creative sizes."
    />
    <ResponsiveAdDemo />
    <PageMasthead
      compact
      kicker="Advertising · Media Pack"
      titleLines={[{ text: "Ad sizes &" }, { text: "specifications", accent: true }]}
      standfirst="Everything you need to book and supply creative across our two flagship parent-facing titles — WhichSchoolAdvisor.com and SchoolsCompared.com — in one shareable place."
    />

    {/* Quick jump + intro */}
    <section className="mx-auto max-w-[1400px] px-5 md:px-8 pb-6">
      <FadeUp>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-sans text-sm font-bold text-foreground/60 mr-1">Jump to:</span>
          <a href="#whichschooladvisor" onClick={jump("whichschooladvisor")} data-testid="jump-wsa" className="inline-flex items-center gap-2 font-sans text-sm font-bold border-2 border-ink text-ink rounded-full px-5 py-2.5 hover:bg-ink hover:text-white transition-colors">
            WhichSchoolAdvisor.com
          </a>
          <a href="#schoolscompared" onClick={jump("schoolscompared")} data-testid="jump-sc" className="inline-flex items-center gap-2 font-sans text-sm font-bold border-2 border-ink text-ink rounded-full px-5 py-2.5 hover:bg-ink hover:text-white transition-colors">
            SchoolsCompared.com
          </a>
          <a href="#gif-guide" onClick={jump("gif-guide")} data-testid="jump-gif" className="inline-flex items-center gap-2 font-sans text-sm font-bold border-2 border-primary text-primary rounded-full px-5 py-2.5 hover:bg-primary hover:text-white transition-colors">
            How to make a compliant ad
          </a>
          <a href="#preflight" onClick={jump("preflight")} data-testid="jump-preflight" className="inline-flex items-center gap-2 font-sans text-sm font-bold border-2 border-primary text-primary rounded-full px-5 py-2.5 hover:bg-primary hover:text-white transition-colors">
            Submit your ad
          </a>
        </div>
      </FadeUp>
    </section>

    <div id="whichschooladvisor" className="scroll-mt-24">
      <AdGuidelines g={WSA} brandName="WhichSchoolAdvisor.com" bg="bg-background" showKicker={false} showJumbo={false} />
    </div>

    <div id="schoolscompared" className="scroll-mt-24">
      <AdGuidelines g={SC} brandName="SchoolsCompared.com" bg="bg-secondary" showKicker={false} showJumbo={false} />
    </div>

    <div id="gif-guide" className="scroll-mt-24">
      <GifHowTo />
    </div>

    {/* CTA */}
    <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <div className="bg-primary text-primary-foreground rounded-3xl p-10 md:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <h2 className="headline text-3xl md:text-5xl uppercase max-w-2xl">
          Ready to reach parents at the moment of decision?
        </h2>
        <Link to="/contact?role=Advertising" data-testid="ad-sizes-cta" className="shrink-0 inline-flex items-center gap-2 font-sans text-base font-bold bg-white text-ink rounded-full pl-8 pr-6 py-4 hover:bg-ink hover:text-white transition-colors">
          Talk to our team <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </div>
  );
};

export default AdSizes;
